/**
 * Color utility functions for determining text contrast
 */

import { PICKER_COLORS } from "../constants/pickerColors";

/**
 * Colors that should not be used for labels
 */
const DISABLED_LABEL_COLORS = ["#ffa600", "#ffffff"];

/**
 * Gets a random color from a given array of color objects
 * Optionally filters out disabled colors
 *
 * @param {Array} colors - Array of color objects with { name, value } structure
 * @param {string[]} disabledColors - Optional array of color values to exclude
 * @returns {string} A random hex color value
 */
export const getRandomColor = (colors, disabledColors = []) => {
  const availableColors = colors.filter(
    (color) => !disabledColors.includes(color.value),
  );
  if (availableColors.length === 0) return colors[0]?.value || "#ffffff";
  return availableColors[Math.floor(Math.random() * availableColors.length)]
    .value;
};

/**
 * Gets a random available color for labels
 * Excludes disabled colors (yellow and white)
 *
 * @returns {string} A random hex color value
 */
export const getRandomLabelColor = () => {
  return getRandomColor(PICKER_COLORS, DISABLED_LABEL_COLORS);
};

/**
 * Determines if a color is light (requires dark text for readability)
 * Uses the relative luminance formula from WCAG guidelines
 *
 * @param {string} color - Hex color string (e.g., "#ffffff" or "#fff")
 * @returns {boolean} True if the color is light and needs dark text
 */
export const isLightColor = (color) => {
  if (!color) return false;

  // Remove # if present
  const hex = color.replace("#", "");

  // Handle 3-character hex codes
  let r, g, b;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    return false;
  }

  // Calculate relative luminance using the formula from WCAG 2.0
  // Using a simplified version that works well for most cases
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return true if the color is light (luminance > 0.5)
  return luminance > 0.5;
};

/**
 * Gets the appropriate text color (dark or light) for a given background color
 *
 * @param {string} backgroundColor - Hex color string
 * @returns {string} "#333333" for light backgrounds, "white" for dark backgrounds
 */
export const getContrastTextColor = (backgroundColor) => {
  return isLightColor(backgroundColor) ? "#333333" : "white";
};
